import type { CaptureResult } from 'posthog-js';

type BeforeSendEvent = CaptureResult | null;

/**
 * Stack frame source patterns that indicate the error originated entirely
 * within the PostHog session recorder or rrweb — not Eudia app code.
 */
const RECORDER_SOURCE_PATTERNS = [
  'posthog-recorder',
  'lazy-recorder',
  '/ph/static/',
];

/**
 * Message patterns for benign network-level TypeErrors that fire when
 * a fetch is interrupted by going offline, closing a tab, or VPN drops.
 * These are not bugs — they are expected infrastructure noise.
 */
const BENIGN_NETWORK_MESSAGES = [
  /failed to fetch/i,
  /network error/i,
  /load failed/i,
  /networkerror when attempting to fetch resource/i,
];

/**
 * Patterns that indicate a stack frame belongs to Eudia app code.
 * If any of these match, the exception was thrown inside our code
 * and must NOT be dropped even if the message looks like a network error.
 */
const APP_STACK_PATTERNS = ['/_next/static/', '@eudia/', '/src/'];

function getExceptionList(
  event: BeforeSendEvent,
): Array<Record<string, unknown>> {
  const props = event?.properties as Record<string, unknown> | null | undefined;
  const list = props?.['$exception_list'];
  return Array.isArray(list) ? (list as Array<Record<string, unknown>>) : [];
}

function getExceptionMessage(event: BeforeSendEvent): string {
  const list = getExceptionList(event);
  const first = list[0];
  if (!first) return '';
  const value = first['value'];
  return typeof value === 'string' ? value : '';
}

/**
 * Real PostHog `Exception.stacktrace` shape is `{ frames: StackFrame[], type: 'raw' }`.
 * Reads frame filenames/paths directly rather than string-matching a JSON blob,
 * so this keeps working if unrelated fields on `StackFrame` change shape.
 */
function getStackTrace(event: BeforeSendEvent): string {
  const list = getExceptionList(event);
  const first = list[0];
  if (!first) return '';
  const stacktrace = first['stacktrace'];
  if (typeof stacktrace === 'string') return stacktrace;

  const frames = (stacktrace as { frames?: unknown } | null | undefined)
    ?.frames;
  if (!Array.isArray(frames)) return '';

  return frames
    .map((frame) => {
      if (!frame || typeof frame !== 'object') return '';
      const { filename, abs_path } = frame as Record<string, unknown>;
      const value = filename ?? abs_path;
      return typeof value === 'string' ? value : '';
    })
    .join(' ');
}

/**
 * Returns true when the exception was thrown entirely within the PostHog
 * session recorder or rrweb — the stack only references recorder scripts,
 * never Eudia app code.
 */
export function isRecorderSdkError(event: BeforeSendEvent): boolean {
  const stack = getStackTrace(event);

  if (!RECORDER_SOURCE_PATTERNS.some((p) => stack.includes(p))) {
    return false;
  }
  // Allow through if the stack also references app code (belt-and-suspenders).
  return !APP_STACK_PATTERNS.some((p) => stack.includes(p));
}

/**
 * Returns true when the error is a benign network TypeError (offline, VPN drop,
 * browser abort) with no Eudia app stack frame. These are expected environment
 * failures, not product bugs.
 */
export function isBenignNetworkError(event: BeforeSendEvent): boolean {
  const message = getExceptionMessage(event);
  if (!BENIGN_NETWORK_MESSAGES.some((re) => re.test(message))) return false;

  // If any app stack frame is present, the error surfaced from our code and
  // should be kept so engineers can investigate the specific call site.
  const stack = getStackTrace(event);
  return !APP_STACK_PATTERNS.some((p) => stack.includes(p));
}

/**
 * PostHog `before_send` filter.
 *
 * Returns `null` (drops the event) for:
 * 1. Exceptions originating entirely within the PostHog recorder / rrweb SDK.
 * 2. Benign network TypeErrors with no Eudia app stack frame.
 *
 * Manual `captureException` calls throw from real app code, so their stack
 * frames match `APP_STACK_PATTERNS` and pass through unchanged.
 */
export function shouldDropPostHogException(event: BeforeSendEvent): boolean {
  if (event?.event !== '$exception') return false;
  return isRecorderSdkError(event) || isBenignNetworkError(event);
}
